<template>
  <div class="home-container bsp-page">
    <div class="home bsp-page__content" v-if="!loading">
      <div v-if="isStaff" class="metrics-page">
        <BspPageHeader
          :breadcrumbs="homeBreadcrumbs"
          title="Operations analytics"
          description="Review WhatsApp usage and cost across all connected accounts."
          eyebrow="Adereso staff"
          icon="tabler:chart-bar"
        />

        <section class="bsp-section metrics-section" aria-labelledby="metrics-title">
          <div class="bsp-section-heading">
            <div>
              <span class="bsp-section-heading__eyebrow">Analytics</span>
              <h2 id="metrics-title">Usage metrics</h2>
              <p>Filter by metric, date range, and phone number before downloading a report.</p>
            </div>
          </div>

          <div class="bsp-filter-grid home-metrics-filters">
            <ASelector
              v-model="filters.analyticsType"
              :items="analyticsOptions"
              item-title="title"
              item-value="value"
              label="Analytics type"
            />
            <div class="bsp-native-field">
              <label for="home-start-date">From</label>
              <input id="home-start-date" v-model="filters.startDate" type="date">
            </div>
            <div class="bsp-native-field">
              <label for="home-end-date">To</label>
              <input id="home-end-date" v-model="filters.endDate" type="date">
            </div>
            <ASelector
              v-model="filters.phoneNumbers"
              :items="phoneNumberOptions"
              item-title="label"
              item-value="value"
              label="Phone numbers"
              placeholder="Select phone numbers"
              multiple
              return-object
              chips
              closable-chips
              :multiple-max-selection-show="2"
            />
          </div>

          <div class="bsp-filter-actions home-metrics-actions">
            <AButton
              color="blue"
              prepend-icon="tabler:filter"
              :loading="loadingMetrics"
              @click="fetchMetrics"
            >
              Apply filters
            </AButton>
            <AButton
              variant="outlined"
              prepend-icon="tabler:download"
              :loading="loadingMetrics"
              @click="downloadReport"
            >
              Download report
            </AButton>
          </div>

          <div v-if="loadingMetrics" class="bsp-inline-loader">
            <SpinnerLoader />
            <span>Loading metrics…</span>
          </div>
          <div v-else-if="aggregatedMetrics.length" class="bsp-table-shell">
            <table>
              <thead>
                <tr>
                  <th>Phone number</th>
                  <th>WABA</th>
                  <th v-if="filters.analyticsType === 'conversation'">Conversations</th>
                  <th v-if="filters.analyticsType === 'pricing'">Messages</th>
                  <th v-if="filters.analyticsType === 'call'">Calls</th>
                  <th v-if="filters.analyticsType === 'call'">Average duration</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(metric, index) in aggregatedMetrics" :key="index">
                  <td>{{ metric.phone_number }}</td>
                  <td>{{ metric.account_name }}</td>
                  <td v-if="filters.analyticsType === 'conversation'">{{ formatNumber(metric.conversation) }}</td>
                  <td v-if="filters.analyticsType === 'pricing'">{{ formatNumber(metric.volume) }}</td>
                  <td v-if="filters.analyticsType === 'call'">{{ formatNumber(metric.count) }}</td>
                  <td v-if="filters.analyticsType === 'call'">{{ formatDuration(metric.average_duration) }}</td>
                  <td>{{ formatCost(metric.cost) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td></td>
                  <td><strong>Total</strong></td>
                  <td v-if="filters.analyticsType === 'conversation'"><strong>{{ formatNumber(totalConversations) }}</strong></td>
                  <td v-if="filters.analyticsType === 'pricing'"><strong>{{ formatNumber(totalVolume) }}</strong></td>
                  <td v-if="filters.analyticsType === 'call'"><strong>{{ formatNumber(totalCalls) }}</strong></td>
                  <td v-if="filters.analyticsType === 'call'"><strong>{{ formatDuration(totalAverageDuration) }}</strong></td>
                  <td><strong>{{ formatCost(totalCost) }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <AEmptyViewCard
            v-else
            icon="tabler:chart-bar-off"
            title="No metrics to display"
            description="Adjust the filters and try again."
          />
        </section>
      </div>

      <div v-else-if="currentUser && currentEstablishment" class="client-view">
        <BspPageHeader
          :breadcrumbs="homeBreadcrumbs"
          :title="currentEstablishment.has_whatsapp_credentials ? 'WhatsApp accounts' : 'Connect WhatsApp'"
          :description="currentEstablishment.has_whatsapp_credentials
            ? `Opening the accounts connected to ${currentEstablishment.name}.`
            : `Choose how you want to connect ${currentEstablishment.name} to Adereso BSP.`"
          eyebrow="WhatsApp setup"
          icon="tabler:brand-whatsapp"
        >
          <template #meta>
            <ATag
              :text="currentEstablishment.name"
              color="brand"
              type="secondary"
              prepend-icon="tabler:building"
            />
          </template>
        </BspPageHeader>

        <section
          v-if="!currentEstablishment.has_whatsapp_credentials"
          class="bsp-onboarding-section"
          aria-labelledby="connect-whatsapp-title"
        >
          <div class="bsp-section-heading">
            <div>
              <span class="bsp-section-heading__eyebrow">Set up a channel</span>
              <h2 id="connect-whatsapp-title">Add a phone number or WABA</h2>
              <p>Choose the flow that matches the current status of your phone number.</p>
            </div>
          </div>

          <div class="bsp-action-grid">
            <v-card class="bsp-action-card" variant="flat">
              <span class="bsp-channel-icon bsp-channel-icon--primary" aria-hidden="true">
                <AIcon icon="tabler:device-mobile-plus" color="primary" size="22" />
              </span>
              <div class="bsp-action-card__copy">
                <h3>New phone number or migration</h3>
                <p>Create or select a WABA, register a new number, or migrate one from another BSP.</p>
              </div>
              <AButton
                color="blue"
                prepend-icon="tabler:plus"
                :disabled="isSignupActionDisabled"
                @click="launchWhatsAppSignup('cloud_api')"
              >
                Register phone number
              </AButton>
            </v-card>

            <v-card class="bsp-action-card" variant="flat">
              <span class="bsp-channel-icon bsp-channel-icon--whatsapp" aria-hidden="true">
                <AIcon icon="tabler:brand-whatsapp" color="success" size="22" />
              </span>
              <div class="bsp-action-card__copy">
                <h3>WhatsApp Business App</h3>
                <p>Connect an active app number and enable Coexistence with Cloud API.</p>
              </div>
              <AButton
                variant="outlined"
                color="blue"
                prepend-icon="tabler:link"
                :disabled="isSignupActionDisabled"
                @click="launchWhatsAppSignup('coexistence')"
              >
                Connect app
              </AButton>
            </v-card>
          </div>
        </section>

        <section v-else class="bsp-section" aria-live="polite">
          <div class="bsp-inline-loader">
            <SpinnerLoader />
            <span>Opening your WhatsApp Business Accounts…</span>
          </div>
        </section>
      </div>

      <div v-else class="no-client-view bsp-empty-page">
        <BspPageHeader
          :breadcrumbs="homeBreadcrumbs"
          title="Set up your establishment"
          description="Create an organization to start managing WhatsApp Business."
          eyebrow="First step"
          icon="tabler:building-plus"
        />
        <v-card class="bsp-form-card" variant="flat">
          <h2>New establishment</h2>
          <p>We'll use this name to identify your operation within the platform.</p>
          <AInput
            v-model="establishmentName"
            label="Establishment name"
            placeholder="E.g. Adereso Chile"
          />
          <AButton color="blue" block prepend-icon="tabler:plus" @click="createEstablishment">
            Create establishment
          </AButton>
        </v-card>
      </div>
    </div>

    <SpinnerLoader v-else />

    <SignupProgressModal
      :visible="showSignupProgressModal"
      :title="signupModalTitle"
      :description="signupModalDescription"
      :steps="signupSteps"
      :error-message="signupErrorMessage"
      :flow-status="signupFlowStatus"
      @close="closeSignupModal"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { POSITION, useToast } from 'vue-toastification'
import {
  AButton,
  AEmptyViewCard,
  AIcon,
  AInput,
  ASelector,
  ATag
} from '@adereso-ai/vue-components'
import AuthService from '@/services/AuthService/AuthService'
import type { User } from '@/services/AuthService/types'
import type { Establishment } from '@/services/EstablishmentService/types'
import type { PhoneNumber } from '@/services/AccountService/types'
import AccountService from '@/services/AccountService/AccountService'
import EstablishmentService from '@/services/EstablishmentService/EstablishmentService'
import MetaService from '@/services/MetaService/MetaService'
import BspPageHeader from '@/components/BspPageHeader.vue'
import SpinnerLoader from '@/components/loaders/SpinnerLoader.vue'
import SignupProgressModal from '@/components/SignupProgressModal.vue'
import { useEmbeddedSignup } from '@/composables/useEmbeddedSignup'
import {
  aggregateMetrics as aggregateMetricsByPhone,
  buildAnalyticsParams,
  computeTotalAverageDuration,
  downloadAnalyticsReport,
  fetchAnalytics,
  filterMetrics,
  formatDuration,
  getReportFilename as resolveReportFilename,
  type AnalyticsType
} from '@/utils/metricsAnalytics'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const loading = ref(true)
const loadingMetrics = ref(false)
const currentUser = ref<User>()
const currentEstablishment = ref<Establishment>()
const establishmentName = ref('')
const phoneNumbers = ref<PhoneNumber[]>([])
const metrics = ref<any[]>([])
const filteredMetrics = ref<any[]>([])
const aggregatedMetrics = ref<any[]>([])

const analyticsOptions = [
  { title: 'Per message', value: 'pricing' },
  { title: 'Conversations', value: 'conversation' },
  { title: 'Calls', value: 'call' }
]

const filters = reactive({
  analyticsType: 'pricing' as AnalyticsType,
  startDate: '',
  endDate: '',
  phoneNumbers: [] as { value: string, label: string }[]
})

const homeBreadcrumbs = [
  {
    title: 'Home',
    disabled: true,
    icon: 'tabler:home'
  }
]

const isStaff = computed(() => currentUser.value?.roles.includes('adereso_staff') === true)

const phoneNumberOptions = computed(() => phoneNumbers.value.map((phone) => ({
  value: phone.clean_display_phone_number,
  label: phone.display_phone_number
})))

const {
  signupFlowStatus,
  signupErrorMessage,
  signupSteps,
  isSignupActionDisabled,
  showSignupProgressModal,
  signupModalTitle,
  signupModalDescription,
  launchWhatsAppSignup,
  closeSignupModal
} = useEmbeddedSignup({
  async onSuccess () {
    await getEstablishment()
    if (currentEstablishment.value?.has_whatsapp_credentials) {
      await router.push({ name: 'whatsapp-business-accounts' })
    }
  }
})

const totalConversations = computed(() => aggregatedMetrics.value.reduce(
  (sum, metric) => sum + (metric.conversation ?? 0),
  0
))

const totalVolume = computed(() => aggregatedMetrics.value.reduce(
  (sum, metric) => sum + (metric.volume ?? 0),
  0
))

const totalCalls = computed(() => aggregatedMetrics.value.reduce(
  (sum, metric) => sum + (metric.count ?? 0),
  0
))

const totalAverageDuration = computed(() => computeTotalAverageDuration(aggregatedMetrics.value))

const totalCost = computed(() => aggregatedMetrics.value.reduce(
  (sum, metric) => sum + metric.cost,
  0
))

function applyFilters () {
  filteredMetrics.value = filterMetrics(metrics.value, filters)
  aggregatedMetrics.value = aggregateMetricsByPhone(filteredMetrics.value, filters.analyticsType)
}

function formatCost (value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value)
}

function formatNumber (value: number) {
  return new Intl.NumberFormat('en-US').format(value)
}

async function getPhoneNumbers () {
  try {
    const accountService = await AccountService.getInstance()
    phoneNumbers.value = await accountService.getAllPhoneNumbers()
    filters.phoneNumbers = phoneNumberOptions.value
  } catch {
    toastError('Error getting WhatsApp Business phone numbers')
  }
}

async function fetchMetrics () {
  loadingMetrics.value = true
  try {
    const metaService = await MetaService.getInstance()
    const params = buildAnalyticsParams(filters)
    const response = await fetchAnalytics(filters.analyticsType, metaService, 'all', params)
    metrics.value = response.data_points
    applyFilters()
  } catch (error) {
    toastError(`Error fetching metrics: ${String(error)}`)
  } finally {
    loadingMetrics.value = false
  }
}

async function downloadReport () {
  loadingMetrics.value = true
  try {
    const metaService = await MetaService.getInstance()
    const params = buildAnalyticsParams(filters, true)
    const response = await downloadAnalyticsReport(filters.analyticsType, metaService, 'all', params)
    const blob = new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', resolveReportFilename(filters.analyticsType))
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    toastError(`Error downloading report: ${String(error)}`)
  } finally {
    loadingMetrics.value = false
  }
}

async function getEstablishment () {
  const establishmentService = await EstablishmentService.getInstance()
  try {
    currentEstablishment.value = await establishmentService.getCurrentEstablishment()
  } catch {
    currentEstablishment.value = undefined
  }
}

async function getCurrentUser () {
  const authService = await AuthService.getInstance()
  currentUser.value = authService.currentUser.value ?? undefined
  if (!currentUser.value) {
    await router.push({ name: 'login', query: route.query })
  }
}

onMounted(async () => {
  await router.isReady()
  await getCurrentUser()

  if (!currentUser.value) {
    loading.value = false
    return
  }

  await getEstablishment()

  if (currentEstablishment.value?.has_whatsapp_credentials) {
    await router.push({ name: 'whatsapp-business-accounts' })
  }

  if (isStaff.value) {
    await getPhoneNumbers()
    await fetchMetrics()
  }

  loading.value = false
})

async function createEstablishment () {
  if (!establishmentName.value.trim()) {
    toastError('Establishment name is required')
    return
  }
  const establishmentService = await EstablishmentService.getInstance()
  await establishmentService.createEstablishment(establishmentName.value.trim())
  await router.push('/')
}

function toastError (errorMessage: string) {
  toast.error(errorMessage, {
    position: POSITION.BOTTOM_LEFT
  })
}
</script>

<style scoped lang="scss">
.metrics-page,
.client-view {
  width: 100%;
}

.home-metrics-filters {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.home-metrics-actions {
  margin-top: 14px;
}

@media (max-width: 960px) {
  .home-metrics-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .home-metrics-filters {
    grid-template-columns: 1fr;
  }

  .home-metrics-actions {
    justify-content: stretch;
  }

  .home-metrics-actions > * {
    flex: 1 1 auto;
  }
}
</style>
